import React, { useState, useEffect, useRef } from 'react';
import './SalesCard.css';
import { extrairErro } from '../../services/apiError';
import { useAuth } from '../../../hooks/useAuth';
import {
  descreverConflito,
  interpretarReversao,
} from '../../services/reversaoErros';

const CRM_API_URL = process.env.REACT_APP_CRM_API_URL || '/api/crm';
const PIPELINE_URL = CRM_API_URL.replace('/api/crm', '/api/pipeline');

const fmt = (v) => (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return '-';
  const date = new Date(d + 'T12:00:00');
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const ICON_MAP = {
  flight: '✈️', hotel: '🏨', car: '🚗', transfer: '🚐',
  bus: '🚌', tour: '🎫', cruise: '🚢', insurance: '🛡️', other: '📦'
};

const fmtDateShort = (d) => {
  if (!d) return '';
  const date = new Date(d + 'T12:00:00');
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const LOSS_REASONS = [
  { key: 'budget', emoji: '💰', label: 'Preço / Orçamento', hint: 'Custo excedeu capacidade financeira' },
  { key: 'competitor', emoji: '🏁', label: 'Perdeu p/ concorrente', hint: 'Comprou em outra agência' },
  { key: 'gave_up', emoji: '🚫', label: 'Desistiu da viagem', hint: 'Mudou de planos ou imprevisto' },
  { key: 'no_response', emoji: '👻', label: 'Sem resposta', hint: 'Parou de responder' },
  { key: 'disintermediation', emoji: '🔓', label: 'Comprou direto', hint: 'Reservou por conta própria' },
  { key: 'misaligned', emoji: '🎯', label: 'Expectativa desalinhada', hint: 'Queria algo diferente do possível' },
];

function FlightDetail({ item }) {
  const d = item.details || {};
  const segments = d.segments || [];
  
  if (!segments.length) {
    return <span className="sales-item-text">{item.title}</span>;
  }

  const first = segments[0];
  const last = segments[segments.length - 1];
  const airline = d.airline || '';
  const flightNum = d.flight_number || '';
  const origin = first.origin || '';
  const dest = last.destination || '';
  const depTime = first.departure_time || '';
  const arrTime = last.arrival_time || '';

  let connectionText = '';
  if (segments.length > 1) {
    const stops = segments.slice(0, -1).map(s => s.destination).filter(Boolean);
    connectionText = stops.length === 1
      ? `Conexão ${stops[0]}`
      : `${stops.length} conexões (${stops.join(', ')})`;
  }

  let baggageText = '';
  if (d.baggage) {
    const b = d.baggage;
    if (b.checked_bags > 0) {
      baggageText = `${b.checked_bags}× despacho`;
      if (b.checked_kg_per_bag) baggageText += ` ${b.checked_kg_per_bag}kg`;
    } else if (b.checked_bags === 0) {
      baggageText = 'Só mão';
    }
  } else if (d.baggage_text) {
    baggageText = d.baggage_text;
  }

  return (
    <div className="sales-flight-detail">
      <div className="sales-flight-route">
        <span className="sales-flight-airline">{airline}{flightNum ? ` · ${flightNum}` : ''}</span>
        <span className="sales-flight-leg">
          {origin} → {dest} · {depTime}–{arrTime}
        </span>
      </div>
      {(connectionText || baggageText) && (
        <div className="sales-flight-extras">
          {connectionText && <span className="sales-flight-connection">🔄 {connectionText}</span>}
          {baggageText && <span className="sales-flight-baggage">🧳 {baggageText}</span>}
        </div>
      )}
    </div>
  );
}

const detailStyle = { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 };
const titleStyle = { fontSize: '0.85rem', fontWeight: 500, color: '#1f2937', lineHeight: 1.3 };
const subStyle = { fontSize: '0.75rem', color: '#6b7280', lineHeight: 1.4, marginTop: '2px' };

function ItemDetail({ item }) {
  const d = item.details || {};
  const type = item.type;

  if (type === 'flight' && d.segments?.length) return <FlightDetail item={item} />;

  if (type === 'hotel') {
    const checkin = d.checkin_date || item.date;
    const checkout = d.checkout_date;
    const room = d.room_type || '';
    const meal = d.meal_plan || '';
    const nights = d.nights || '';
    return (
      <div style={detailStyle}>
        <span style={titleStyle}>{item.title}</span>
        <span style={subStyle}>
          {checkin && checkout ? `${fmtDateShort(checkin)} → ${fmtDateShort(checkout)}` : fmtDateShort(checkin)}
          {nights ? ` (${nights}n)` : ''}{room ? ` · ${room}` : ''}{meal ? ` · ${meal}` : ''}
        </span>
      </div>
    );
  }

  if (type === 'bus') {
    const depTime = d.departure_time || '';
    const arrTime = d.arrival_time || '';
    const seat = d.seat_class || '';
    const company = d.company || '';
    const duration = d.duration || '';
    return (
      <div style={detailStyle}>
        <span style={titleStyle}>{item.title}</span>
        <span style={subStyle}>
          {fmtDateShort(item.date)}{depTime ? ` · ${depTime}` : ''}{arrTime ? `–${arrTime}` : ''}
          {duration ? ` (${duration})` : ''}{seat ? ` · ${seat}` : ''}{company ? ` · ${company}` : ''}
        </span>
      </div>
    );
  }

  if (type === 'transfer') {
    const origin = d.origin_name || '';
    const dest = d.destination_name || '';
    const vehicle = d.vehicle_type || '';
    const pickup = d.pickup_time || '';
    return (
      <div style={detailStyle}>
        <span style={titleStyle}>{item.title}</span>
        <span style={subStyle}>
          {fmtDateShort(item.date)}{pickup ? ` · ${pickup}` : ''}{vehicle ? ` · ${vehicle}` : ''}
          {origin && dest ? ` · ${origin} → ${dest}` : ''}
        </span>
      </div>
    );
  }

  if (type === 'car') {
    const pickup = d.pickup_date || item.date;
    const dropoff = d.dropoff_date || '';
    const pickupLoc = d.pickup_location || '';
    const days = d.days || '';
    return (
      <div style={detailStyle}>
        <span style={titleStyle}>{item.title}</span>
        <span style={subStyle}>
          {pickup && dropoff ? `${fmtDateShort(pickup)} → ${fmtDateShort(dropoff)}` : fmtDateShort(pickup)}
          {days ? ` (${days}d)` : ''}{pickupLoc ? ` · ${pickupLoc}` : ''}
        </span>
      </div>
    );
  }

  if (type === 'insurance') {
    const company = d.company || '';
    const plan = d.plan_name || '';
    const coverage = d.coverage_amount || 0;
    const currency = d.coverage_currency || 'USD';
    const start = d.start_date || item.date;
    const end = d.end_date || '';
    return (
      <div style={detailStyle}>
        <span style={titleStyle}>{item.title}</span>
        <span style={subStyle}>
          {start && end ? `${fmtDateShort(start)} → ${fmtDateShort(end)}` : ''}
          {company ? ` · ${company}` : ''}{plan ? ` · ${plan}` : ''}
          {coverage > 0 ? ` · ${currency} ${coverage.toLocaleString('pt-BR')}` : ''}
        </span>
      </div>
    );
  }

  return (
    <div style={detailStyle}>
      <span style={titleStyle}>{item.title || item.type}</span>
      {(item.date || item.description) && (
        <span style={subStyle}>
          {item.date ? fmtDateShort(item.date) : ''}
          {item.description ? ` · ${item.description}` : ''}
        </span>
      )}
    </div>
  );
}

function QuoteItems({ q }) {
  const items = q.items || [];
  const markup = parseFloat(q.markup) || 0;
  const totalPrice = parseFloat(q.total_price) || 0;
  const finalPrice = totalPrice + markup;

  return (
    <>
      {items.length > 0 && (
        <div className="sales-quote-items">
          {items.map((item, i) => (
            <div key={i} className="sales-item-row">
              <span className="sales-item-icon">{ICON_MAP[item.type] || '📦'}</span>
              <ItemDetail item={item} />
              {item.price > 0 && (
                <span className="sales-item-price">R$ {fmt(item.price)}</span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="sales-quote-price-section">
        {markup > 0 ? (
          <>
            <div className="sales-price-line">
              <span>Base</span>
              <span>R$ {fmt(totalPrice)}</span>
            </div>
            <div className="sales-price-line sales-price-markup">
              <span>Markup</span>
              <span>R$ {fmt(markup)}</span>
            </div>
            <div className="sales-price-line sales-price-total">
              <span>Total cliente</span>
              <span>R$ {fmt(finalPrice)}</span>
            </div>
          </>
        ) : (
          <div className="sales-price-line sales-price-total">
            <span>Valor</span>
            <span>R$ {fmt(finalPrice)}</span>
          </div>
        )}
      </div>
    </>
  );
}


/**
 * SalesCard - Ficha de vendas do contato.
 * Propostas com múltiplas opções usam abas para alternar.
 */
export default function SalesCard({ contactId, contactName, onClose }) {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lostModal, setLostModal] = useState(null);
  const [lossReason, setLossReason] = useState('');
  const [lossDetail, setLossDetail] = useState('');
  const [activeAlt, setActiveAlt] = useState({});   // destino_id → índice em exibição
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [undoModal, setUndoModal] = useState(null);      // {order_id, order_status}
  const [undoReason, setUndoReason] = useState('');
  const [undoErro, setUndoErro] = useState(null);
  const [undoConflitos, setUndoConflitos] = useState([]);

  // Identidade de quem está operando o card. Vai no header das ações que
  // gravam autoria (fechar e desfazer) para que a auditoria não registre
  // `null`. Quem toma decisão operacional deixa rastro.
  const { profile } = useAuth();
  const sellerId = profile?.seller_id || null;
  const comAutor = (base = {}) => (
    sellerId ? { ...base, 'X-Caller-Seller-Id': sellerId } : { ...base }
  );
  const cardRef = useRef(null);

  useEffect(() => {
    if (!contactId) return;
    fetchOpportunities();
  }, [contactId]);

  useEffect(() => {
    const handler = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  useEffect(() => {
    const handler = () => { if (document.visibilityState === 'visible') fetchOpportunities(); };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [contactId]);

  const fetchOpportunities = async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${PIPELINE_URL}/contacts/${contactId}/quotes`);
      if (resp.ok) setOpportunities(await resp.json());
    } catch (e) { console.error('Erro ao buscar oportunidades:', e); }
    finally { setLoading(false); }
  };

  // Radio DENTRO de um destino. Não toca nos demais destinos.
  const selectAlternative = async (destinoId, alternativeId) => {
    setBusy(true);
    try {
      const resp = await fetch(`${PIPELINE_URL}/quotes/${destinoId}/select-option`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alternative_id: alternativeId })
      });
      const erro = await extrairErro(resp);
      if (erro) {
        alert(erro);
        await fetchOpportunities();   // realinha a tela com o estado real
        return;
      }
      await fetchOpportunities();
    } catch (e) {
      console.error(e);
      alert('Não foi possível registrar a seleção. Tente novamente.');
    }
    finally { setBusy(false); }
  };

  // Fechamento pertence à OPORTUNIDADE — um clique, uma venda.
  const closeOpportunity = async (orderId, status, extra = {}) => {
    setBusy(true);
    try {
      const resp = await fetch(`${PIPELINE_URL}/orders/${orderId}/close`, {
        method: 'POST',
        headers: comAutor({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ status, ...extra })
      });
      if (!resp.ok) {
        const err = await resp.json();
        alert(err.error || 'Erro ao fechar oportunidade');
        return;
      }
      setLostModal(null); setLossReason(''); setLossDetail('');
      await fetchOpportunities();
    } catch (e) { console.error(e); }
    finally { setBusy(false); }
  };

  // Desfazer — restaura o estado anterior ao fechamento.
  //
  // O que a tela faz depende do desfecho, e a regra mora em
  // `reversaoErros.js`: 404 e 409-já-revertida significam que este card
  // está desatualizado (recarrega e avisa); 409-conflito significa que o
  // card está certo e alguém mexeu na oportunidade depois do fechamento —
  // aí o modal fica aberto com a lista, porque o vendedor precisa ler
  // antes de decidir o que fazer.
  const undoOpportunity = async (orderId, reason) => {
    setBusy(true);
    setUndoErro(null);
    setUndoConflitos([]);
    try {
      const resp = await fetch(`${PIPELINE_URL}/orders/${orderId}/reopen`, {
        method: 'POST',
        headers: comAutor({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ reason }),
      });
      const desfecho = await interpretarReversao(resp);

      if (desfecho.ok || desfecho.recarregar) {
        setUndoModal(null);
        setUndoReason('');
        await fetchOpportunities();
        if (!desfecho.ok) alert(desfecho.mensagem);
        return;
      }

      setUndoErro(desfecho.mensagem);
      setUndoConflitos(desfecho.conflitos);
    } catch (e) {
      console.error(e);
      setUndoErro('Não foi possível desfazer. Tente novamente.');
    }
    finally { setBusy(false); }
  };

  // Desmembra um DESTINO inteiro, com todas as suas alternativas.
  const splitDestino = async (orderId, destino) => {
    const ok = window.confirm(
      `Desmembrar "${destino.destination}" para uma oportunidade separada?\n\n` +
      `O destino sai desta negociação com todas as suas alternativas e passa a ` +
      `ter ciclo de venda próprio.`
    );
    if (!ok) return;
    setBusy(true);
    try {
      const resp = await fetch(`${PIPELINE_URL}/orders/${orderId}/split`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ destino_id: destino.destino_id })
      });
      if (!resp.ok) {
        const err = await resp.json();
        alert(err.error || 'Erro ao desmembrar');
        return;
      }
      setMenuOpen(null);
      await fetchOpportunities();
    } catch (e) { console.error(e); }
    finally { setBusy(false); }
  };

  const openQuote = (quoteId) => window.open(`/app/cotacao/${quoteId}`, '_blank');

  const statusConfig = {
    draft:     { bg: '#f3f4f6', color: '#6b7280', label: 'Rascunho' },
    published: { bg: '#fef3c7', color: '#92400e', label: 'Publicada' },
    sent:      { bg: '#dbeafe', color: '#1e40af', label: '📲 Enviada' },
    won:       { bg: '#d1fae5', color: '#065f46', label: '✓ Ganho' },
    lost:      { bg: '#fee2e2', color: '#991b1b', label: '✗ Perdido' },
    paused:    { bg: '#fef9c3', color: '#854d0e', label: '⏸ Pausada' }
  };

  const getTravelers = (q) => {
    const parts = [];
    if (q.adults) parts.push(`${q.adults} adulto${q.adults > 1 ? 's' : ''}`);
    if (q.children) parts.push(`${q.children} criança${q.children > 1 ? 's' : ''}`);
    if (q.infants) parts.push(`${q.infants} bebê${q.infants > 1 ? 's' : ''}`);
    return parts.length ? parts.join(', ') : null;
  };

  return (
    <div className="sales-card-overlay">
      <div className="sales-card" ref={cardRef}>
        <div className="sales-card-header">
          <div>
            <h3>💼 Vendas</h3>
            <p className="sales-card-contact">{contactName}</p>
          </div>
          <button className="sales-card-close" onClick={onClose}>✕</button>
        </div>

        <div className="sales-card-body">
          {loading ? (
            <div className="sales-card-loading">Carregando oportunidades...</div>
          ) : opportunities.length === 0 ? (
            <div className="sales-card-empty">
              <span className="sales-card-empty-icon">📋</span>
              <p>Nenhuma cotação vinculada</p>
              <p className="sales-card-hint">Crie uma cotação pelo Assistente vinculando a este contato</p>
            </div>
          ) : (
            opportunities.map(opp => {
              const oc = statusConfig[opp.order_status === 'open' ? 'sent' : opp.order_status];
              const podeDesmembrar = opp.is_open && opp.destinos.length > 1;

              return (
                <div key={opp.order_id || opp.destinos[0].id} className="sales-quote-card">
                  {/* Cabeçalho da OPORTUNIDADE */}
                  <div className="sales-quote-top">
                    <span className="sales-quote-dest">
                      💼 Oportunidade
                      {opp.destinos.length > 1 && ` — ${opp.destinos.length} destinos`}
                    </span>
                    <span className="sales-quote-badge" style={{ backgroundColor: oc?.bg, color: oc?.color }}>
                      {opp.order_status === 'open' ? 'Em negociação' : oc?.label}
                    </span>
                  </div>

                  {/* Cada DESTINO é um grupo de seleção independente */}
                  {opp.destinos.map(destino => {
                    const alternativas = destino.alternativas || [];
                    const selId = destino.selected_option_id;
                    const idx = activeAlt[destino.destino_id] ??
                      Math.max(0, alternativas.findIndex(a => a.id === selId));
                    const emExibicao = alternativas[idx] || alternativas[0];
                    const travelers = getTravelers(emExibicao);
                    const sc = statusConfig[emExibicao.pipeline_status || 'draft'];

                    return (
                      <div key={destino.destino_id} className="sales-destino-block">
                        <div className="sales-destino-header">
                          <span className="sales-destino-name">✈️ {destino.destination || 'Sem destino'}</span>
                          {podeDesmembrar && (
                            <span className="sales-destino-menu">
                              <button
                                className="sales-destino-menu-btn"
                                onClick={() => setMenuOpen(menuOpen === destino.destino_id ? null : destino.destino_id)}
                              >⋯</button>
                              {menuOpen === destino.destino_id && (
                                <button
                                  className="sales-destino-split"
                                  disabled={busy}
                                  onClick={() => splitDestino(opp.order_id, destino)}
                                >
                                  Desmembrar oportunidade
                                </button>
                              )}
                            </span>
                          )}
                        </div>

                        {/* Radio: uma alternativa por destino */}
                        <div className="sales-alt-list">
                          {alternativas.map((alt, i) => {
                            const preco = parseFloat(alt.total_price || 0) + parseFloat(alt.markup || 0);
                            const marcada = alt.id === selId;
                            return (
                              <label
                                key={alt.id}
                                className={`sales-alt${marcada ? ' selected' : ''}${idx === i ? ' viewing' : ''}`}
                                onMouseEnter={() => setActiveAlt(prev => ({ ...prev, [destino.destino_id]: i }))}
                              >
                                <input
                                  type="radio"
                                  name={`destino-${destino.destino_id}`}
                                  checked={marcada}
                                  disabled={!opp.is_open || busy}
                                  onChange={() => selectAlternative(destino.destino_id, alt.id)}
                                />
                                <span className="sales-alt-label">Alternativa {String.fromCharCode(65 + i)}</span>
                                <span className="sales-alt-price">R$ {fmt(preco)}</span>
                                {marcada && opp.order_status === 'won' && (
                                  <span className="sales-alt-tag sold">vendida</span>
                                )}
                                {!marcada && opp.order_status === 'won' && (
                                  <span className="sales-alt-tag neutral">não escolhida</span>
                                )}
                                <button
                                  type="button"
                                  className="sales-alt-open"
                                  onClick={(e) => { e.preventDefault(); openQuote(alt.id); }}
                                >Abrir ↗</button>
                              </label>
                            );
                          })}
                        </div>

                        {/* Detalhe da alternativa em exibição */}
                        <div className="sales-quote-meta">
                          {/* Período vem derivado dos blocos pela API
                              (period_start/period_end). O card não calcula,
                              não interpreta e não lê start_date/end_date —
                              só renderiza. period_end pode ser null quando
                              nenhum bloco tem fim conhecido (ex.: cruzeiro). */}
                          {emExibicao.period_start && (
                            <span className="sales-meta-item">
                              📅 {fmtDate(emExibicao.period_start)}
                              {emExibicao.period_end ? ` → ${fmtDate(emExibicao.period_end)}` : ''}
                            </span>
                          )}
                          {travelers && <span className="sales-meta-item">👥 {travelers}</span>}
                          <span className="sales-meta-item" style={{ color: sc?.color }}>{sc?.label}</span>
                        </div>
                        <QuoteItems q={emExibicao} />
                      </div>
                    );
                  })}

                  {/* Composição da venda — soma das selecionadas */}
                  <div className="sales-opp-total">
                    <span>
                      {opp.order_status === 'won' ? 'Venda' : 'Selecionado'}
                      {opp.selected_count > 1 && ` (${opp.selected_count} destinos)`}
                    </span>
                    <strong>R$ {fmt(opp.selected_total)}</strong>
                  </div>

                  {opp.closed_reason && (
                    <div className="sales-quote-reason">💬 {opp.closed_reason}</div>
                  )}

                  {opp.is_open && (
                    <div className="sales-quote-actions">
                      <button
                        className="sales-btn-won"
                        disabled={busy || opp.selected_count === 0}
                        title={opp.selected_count === 0
                          ? 'Selecione ao menos uma alternativa'
                          : `Fechar a oportunidade por R$ ${fmt(opp.selected_total)}`}
                        onClick={() => closeOpportunity(opp.order_id, 'won')}
                      >
                        ✓ Ganho — R$ {fmt(opp.selected_total)}
                      </button>
                      <button
                        className="sales-btn-lost"
                        disabled={busy}
                        onClick={() => setLostModal(opp.order_id)}
                      >✗ Perdido</button>
                    </div>
                  )}

                  {/* Oportunidade fechada: desfazer, ou a explicação de por
                      que não dá. `can_undo` vem da API — a tela não deduz.
                      Campo ausente (bundle antigo) é falsy e cai na
                      explicação, nunca num botão que falha. */}
                  {!opp.is_open && (
                    <div className="sales-quote-actions">
                      {opp.can_undo ? (
                        <button
                          className="sales-btn-undo"
                          disabled={busy}
                          title="Restaura o estado que a oportunidade tinha antes do fechamento"
                          onClick={() => {
                            setUndoModal({ order_id: opp.order_id, order_status: opp.order_status });
                            setUndoReason('');
                            setUndoErro(null);
                            setUndoConflitos([]);
                          }}
                        >
                          ↩ Desfazer {opp.order_status === 'won' ? 'ganho' : 'perda'}
                        </button>
                      ) : (
                        <span className="sales-undo-bloqueado">
                          {opp.undo_block_reason === 'sem_snapshot'
                            ? 'Fechada antes do registro de reversão — não é possível desfazer automaticamente.'
                            : 'Não é possível desfazer esta oportunidade.'}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {lostModal && (
          <div className="sales-lost-modal">
            <h4>Motivo da perda</h4>
            <div className="sales-loss-reasons">
              {LOSS_REASONS.map(r => (
                <button
                  key={r.key}
                  className={`sales-loss-reason-btn${lossReason === r.key ? ' active' : ''}`}
                  onClick={() => setLossReason(r.key)}
                >
                  <span className="sales-loss-emoji">{r.emoji}</span>
                  <span className="sales-loss-label">{r.label}</span>
                  <span className="sales-loss-hint">{r.hint}</span>
                </button>
              ))}
            </div>
            {lossReason && (
              <input
                type="text"
                value={lossDetail}
                onChange={(e) => setLossDetail(e.target.value)}
                placeholder="Detalhe opcional..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') closeOpportunity(lostModal, 'lost', { loss_reason: lossReason, reason: lossDetail || undefined });
                }}
              />
            )}
            <div className="sales-lost-btns">
              <button onClick={() => { setLostModal(null); setLossReason(''); setLossDetail(''); }}>Cancelar</button>
              <button
                className="confirm"
                disabled={!lossReason || busy}
                style={{ opacity: lossReason ? 1 : 0.4 }}
                onClick={() => closeOpportunity(lostModal, 'lost', { loss_reason: lossReason, reason: lossDetail || undefined })}
              >Confirmar</button>
            </div>
          </div>
        )}

        {undoModal && (
          <div className="sales-lost-modal">
            <h4>Desfazer {undoModal.order_status === 'won' ? 'ganho' : 'perda'}</h4>
            <p className="sales-undo-explica">
              A oportunidade volta ao estado anterior ao fechamento: situação
              das cotações e motivo de perda. A alternativa escolhida pelo
              cliente é mantida. Este fechamento continua registrado no
              histórico.
            </p>
            <textarea
              className="sales-undo-motivo"
              value={undoReason}
              onChange={(e) => setUndoReason(e.target.value)}
              placeholder="Motivo da reversão (obrigatório). Ex.: marquei o lead errado"
              rows={3}
            />
            {undoErro && (
              <div className="sales-undo-erro">{undoErro}</div>
            )}
            {undoConflitos.length > 0 && (
              <ul className="sales-undo-conflitos">
                {undoConflitos.map((c, i) => (
                  <li key={i}>{descreverConflito(c)}</li>
                ))}
              </ul>
            )}
            <div className="sales-lost-btns">
              <button
                onClick={() => {
                  setUndoModal(null); setUndoReason('');
                  setUndoErro(null); setUndoConflitos([]);
                }}
              >Cancelar</button>
              <button
                className="confirm"
                disabled={!undoReason.trim() || busy}
                style={{ opacity: undoReason.trim() && !busy ? 1 : 0.4 }}
                onClick={() => undoOpportunity(undoModal.order_id, undoReason.trim())}
              >Confirmar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
